import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { NewbornIPPGestationalAgeQuestionsLinkIds } from "@/encounters/constants/newborn-ipp";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";

const GestationalAgeDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.NewbornIPPGestationalAge,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const previousGestationalAgeAnswers = getQuestionnaireAnswers(previousVersion);
  const currentGestationalAgeAnswers = getQuestionnaireAnswers(currentVersion);

  const previousGestationalAge =
    previousGestationalAgeAnswers[
      NewbornIPPGestationalAgeQuestionsLinkIds.GestationalAge
    ]?.valueInteger?.toString();
  const currentGestationalAge =
    currentGestationalAgeAnswers[
      NewbornIPPGestationalAgeQuestionsLinkIds.GestationalAge
    ]?.valueInteger?.toString();

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Gestational Age</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="0">
              <TextWrapper fw={600}>Gestational Age</TextWrapper>
              <WordsDiff
                showRemoved
                current={currentGestationalAge}
                previous={previousGestationalAge}
              />
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="0">
              <TextWrapper fw={600}>Gestational Age</TextWrapper>
              <WordsDiff current={currentGestationalAge} previous={previousGestationalAge} />
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default GestationalAgeDiff;
